import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { KEY_ENTER, KEY_RETURN } from 'keycode-js';
import { SuiContainer, SuiTextInput, SuiCheckbox, SuiButton, SuiErrorText, SuiTooltipPopover } from '@splashthat/sui';
import AddOutline from 'common/icons/AddOutline';

import { getFormNode, getFormNodeOptions, getFormNodeEditingLocked } from 'common/entities/formNode/selectors';
import { INPUT_TYPE_MULTI_SELECT } from 'common/entities/formNode/inputTypes';
import { getEditFormNodeId, getFormErrors } from 'common/compounds/FormBuilder/selectors';

import FormEditorEditQuestionOptionDragAndDropWrapper from './FormEditorEditQuestionOptionDragAndDropWrapper';
import FormEditorEditQuestionOption from './FormEditorEditQuestionOption';

import { setIncludeOtherOption } from '../../actions';
import {
    addFormNodeOption,
    removeFormNodeOption,
    updateFormNodeOption,
    moveFormNodeOption,
    setFormNodeOptionSelected,
    updateFormNodeOptionResponseLimit,
    addMultipleFormNodeOptions,
} from '../../actions/nodeOptionActions';

import { NO_OPTIONS } from '../../constants/errorCodes';

const NEW_OPTIONS_INPUT_PLACEHOLDER = 'Type Options Here';
const NEW_OPTIONS_BUTTON_TITLE = 'Done';
const INCLUDE_OTHER_LABEL = 'Include "Other" option';
const NO_OPTION_ERROR = 'At least an option is needed to use this question type.';
const REPEATED_OPTION_ERROR = 'This option value already exists.';
export const DISABLED_OTHER = 'This option is not available for protected fields';

const baseClassName = 'FormEditorEditQuestionOptions';

class FormEditorEditQuestionOptions extends Component {
    static propTypes = {
        allowMultipleSelected: PropTypes.bool.isRequired,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.string,
                responseLimit: PropTypes.number,
            }),
        ).isRequired,
        includeOther: PropTypes.bool.isRequired,
        editingLocked: PropTypes.bool.isRequired,
        editNodeId: PropTypes.string.isRequired,
        addFormNodeOption: PropTypes.func.isRequired,
        removeFormNodeOption: PropTypes.func.isRequired,
        updateFormNodeOption: PropTypes.func.isRequired,
        moveFormNodeOption: PropTypes.func.isRequired,
        setFormNodeOptionSelected: PropTypes.func.isRequired,
        updateFormNodeOptionResponseLimit: PropTypes.func.isRequired,
        showEditValueInputs: PropTypes.bool.isRequired,
        setIncludeOtherOption: PropTypes.func.isRequired,
        addMultipleFormNodeOptions: PropTypes.func.isRequired,
        errors: PropTypes.arrayOf(PropTypes.string),
        protectedField: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        errors: null,
    };

    constructor(props) {
        super(props);

        this.state = {
            openOptionId: null,
            newOptionInputValue: null,
            // When reordering, the current drop target option.
            dropOptionId: null,
            // When reordering, the 0 to 1 percentage that cursor has over drop target.
            dropYPosition: null,
        };
    }

    handlePasteOptions = (event) => {
        const pastedItems = event.clipboardData.getData('text/plain').split('\n').filter(Boolean);

        if (pastedItems.length > 1) {
            event.preventDefault();
            this.props.addMultipleFormNodeOptions(this.props.editNodeId, pastedItems);
        }
    };

    handleNewOptionButtonClick = (event) => {
        const { newOptionInputValue } = this.state;
        if (newOptionInputValue) {
            this.handleAddOption(newOptionInputValue);
        }
    };

    handleNewOptionInputKeyPress = (event) => {
        if (event.which === KEY_ENTER || event.which === KEY_RETURN) {
            this.handleAddOption(event.target.value);
        }
    };

    handleNewOptionInputValueChange = (event, value) => {
        this.setState((state) => ({
            ...state,
            newOptionInputValue: value,
            isOptionRepeated: false,
        }));
    };

    /**
     * @param {string} optionId
     * @param {number} responseLimit
     */
    handleUpdateResponseLimit = (optionId, responseLimit) => {
        this.props.updateFormNodeOptionResponseLimit(this.props.editNodeId, optionId, responseLimit);
    };

    handleIncludeOtherChange = (event, checked) => {
        this.props.setIncludeOtherOption(this.props.editNodeId, checked);
    };

    handleRemoveOption = (optionId) => {
        this.props.removeFormNodeOption(this.props.editNodeId, optionId);
    };

    onCollapsibleOpened = (optionId) => {
        this.setState((state) => ({
            ...state,
            openOptionId: optionId !== state.openOptionId ? optionId : null,
        }));
    };

    isOptionRepeated(optionLabel) {
        return !!this.props.options.filter(({ label }) => label === optionLabel).length;
    }

    handleAddOption = (label) => {
        const trimmedLabel = label && label.trim();
        const isOptionRepeated = this.isOptionRepeated(trimmedLabel);

        if (isOptionRepeated) {
            this.setState({ isOptionRepeated: true });
            return;
        }

        if (trimmedLabel) {
            const value = trimmedLabel;
            this.props.addFormNodeOption(this.props.editNodeId, trimmedLabel, value);
            this.setState((state) => ({ ...state, newOptionInputValue: '' }));
        }
    };

    handleUpdateOption = (optionId, option) => {
        this.props.updateFormNodeOption(this.props.editNodeId, optionId, option);
    };

    handleSetOptionSelected = (optionId, selected) => {
        this.props.setFormNodeOptionSelected(this.props.editNodeId, optionId, selected);
    };

    beginDragOption = (optionId) => {};

    endDragOption = (optionId) => {
        const dropIndex = this.getDropIndex();
        if (dropIndex >= 0) {
            this.props.moveFormNodeOption(this.props.editNodeId, optionId, dropIndex);
        }
        this.setState((state) => ({
            ...state,
            dropOptionId: null,
            dropYPosition: null,
        }));
    };

    hoverOption = (dropOptionId, dropYPosition) => {
        this.setState((state) => ({
            ...state,
            dropOptionId,
            dropYPosition,
        }));
    };

    /**
     * Returns the current drop index, of the hovered option, when reordering is active.
     * @return {number|null}
     */
    getDropIndex() {
        const { dropOptionId, dropYPosition } = this.state;
        if (!dropOptionId) {
            return null;
        }
        const { options } = this.props;
        const optionIds = options.map((option) => option.id);
        return optionIds.indexOf(dropOptionId) + (dropYPosition > 0.5 ? 1 : 0);
    }
    shouldComponentUpdate(nextProps, nextState) {
        // This is avoiding unnecesary re-renders when some prop change its reference but it's not updated
        // TODO: Improve selectors
        return this.state !== nextState || !isEqual(this.props, nextProps);
    }

    render() {
        const {
            allowMultipleSelected,
            options,
            includeOther,
            showEditValueInputs,
            errors,
            editingLocked,
            protectedField,
        } = this.props;
        const { openOptionId, newOptionInputValue, dropOptionId, dropYPosition } = this.state;

        const hasNoOptionsError = errors.indexOf(NO_OPTIONS) !== -1;

        return (
            <SuiContainer onPaste={this.handlePasteOptions} variation="flex-vertical flex-inherit">
                {options.length > 0 && (
                    <SuiContainer variation="flex-group">
                        {options.map((option) => (
                            <FormEditorEditQuestionOptionDragAndDropWrapper
                                key={option.id}
                                optionId={option.id}
                                beginDragOption={this.beginDragOption}
                                endDragOption={this.endDragOption}
                                hoverOption={this.hoverOption}
                            >
                                {(connectDragSource) => (
                                    <FormEditorEditQuestionOption
                                        connectDragSource={connectDragSource}
                                        optionId={option.id}
                                        optionLabel={option.label}
                                        optionValue={option.value}
                                        responseLimit={option.responseLimit}
                                        allowMultipleSelected={allowMultipleSelected}
                                        optionSelected={option.selected}
                                        addOption={this.handleAddOption}
                                        removeOption={this.handleRemoveOption}
                                        updateOption={this.handleUpdateOption}
                                        setOptionSelected={this.handleSetOptionSelected}
                                        updateOptionResponseLimit={this.handleUpdateResponseLimit}
                                        isOpen={openOptionId === option.id}
                                        onCollapsibleOpened={this.onCollapsibleOpened}
                                        showEditValueInputs={showEditValueInputs}
                                        isDropTargetActive={dropOptionId === option.id}
                                        dropTargetYPosition={dropYPosition}
                                        disabled={editingLocked}
                                    />
                                )}
                            </FormEditorEditQuestionOptionDragAndDropWrapper>
                        ))}
                    </SuiContainer>
                )}
                {hasNoOptionsError && !options.length && <SuiErrorText text={NO_OPTION_ERROR} />}
                <SuiContainer variation="flex-group">
                    <SuiTextInput
                        value={newOptionInputValue}
                        placeholder={NEW_OPTIONS_INPUT_PLACEHOLDER}
                        onChange={this.handleNewOptionInputValueChange}
                        onKeyPress={this.handleNewOptionInputKeyPress}
                        fullWidth
                        disabled={editingLocked}
                        iconRight={
                            newOptionInputValue && (
                                <SuiButton
                                    title={NEW_OPTIONS_BUTTON_TITLE}
                                    onClick={this.handleNewOptionButtonClick}
                                    icon={AddOutline}
                                    variation="icon text"
                                />
                            )
                        }
                    />
                </SuiContainer>
                {this.state.isOptionRepeated && <SuiErrorText text={REPEATED_OPTION_ERROR} />}
                {protectedField ? (
                    <SuiTooltipPopover align="left" content={DISABLED_OTHER} disabled>
                        <SuiContainer
                            className={`${baseClassName}__other`}
                            variation={this.state.isOptionRepeated ? '' : 'flex-group'}
                            data-qa="checkbox-container"
                        >
                            <SuiCheckbox label={INCLUDE_OTHER_LABEL} disabled />
                        </SuiContainer>
                    </SuiTooltipPopover>
                ) : (
                    <SuiContainer
                        className={`${baseClassName}__other`}
                        variation={this.state.isOptionRepeated ? '' : 'flex-group'}
                    >
                        <SuiCheckbox
                            label={INCLUDE_OTHER_LABEL}
                            checked={includeOther}
                            disabled={editingLocked}
                            onChange={this.handleIncludeOtherChange}
                        />
                    </SuiContainer>
                )}
            </SuiContainer>
        );
    }
}

const mapStateToProps = (state, props) => {
    const editFormNodeId = getEditFormNodeId(state);
    const editFormNode = getFormNode(state, editFormNodeId);

    return {
        editNodeId: getEditFormNodeId(state),
        options: getFormNodeOptions(state, editFormNodeId),
        includeOther: get(editFormNode, 'config.includeOther', false),
        allowMultipleSelected: get(editFormNode, 'inputType') === INPUT_TYPE_MULTI_SELECT,
        errors: getFormErrors(state),
        editingLocked: editFormNode ? getFormNodeEditingLocked(state, editFormNodeId) : false,
    };
};

const mapDispatchToProps = {
    addMultipleFormNodeOptions,
    setIncludeOtherOption,
    updateFormNodeOptionResponseLimit,
    removeFormNodeOption,
    addFormNodeOption,
    updateFormNodeOption,
    moveFormNodeOption,
    setFormNodeOptionSelected,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormEditorEditQuestionOptions);
